import React, { useState, useCallback, useEffect } from 'react';
import { Modal, H1, styled, ant, List, Button, Flex } from '@erecruit/ui-kit';
import { connect } from 'react-redux';
import { Intl } from '@erecruit/core';
import { useHistory } from 'react-router-dom';
import { Notification } from '@erecruit/modules';

import { NotificationListItem } from './NotificationStand';
import { notificationSelectors, notificationActions } from '../state/reducer';

const { FormattedMessage } = Intl;

interface NotificationPageProps {
  notifications: Notification[];
  className: string;
  fullHistoryLoaded: boolean;
  isLoading: boolean;
  fetchHistory: typeof notificationActions.fetchHistory;
  resetHistory: typeof notificationActions.resetHistory;
}

const PAGE_SIZE = 10;

export const StyledNotificationPage = styled(
  ({
    notifications,
    className,
    fullHistoryLoaded,
    isLoading,
    fetchHistory,
    resetHistory,
  }: NotificationPageProps) => {
    const history = useHistory();
    const [pagination, setPagination] = useState({
      offset: 0,
      limit: PAGE_SIZE,
    });

    const loadHistory = useCallback(() => {
      const newPagination = {
        offset: pagination.offset + PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      setPagination(newPagination);
    }, [pagination]);

    const loadMoreHistory =
      !isLoading && !fullHistoryLoaded ? (
        <Flex padding={4} align="center" justify="center">
          <Button onClick={loadHistory}>
            <FormattedMessage id={messageIds.vms.notificationPopup.loadMore} />
          </Button>
        </Flex>
      ) : null;

    useEffect(() => {
      fetchHistory(pagination);
    }, [fetchHistory, pagination]);

    const onModalClose = useCallback(() => {
      resetHistory();
      history.goBack();
    }, [resetHistory, history]);

    return (
      <Modal
        width={932}
        title={
          <H1>
            <FormattedMessage
              id={messageIds.vms.notifications.notificationModalTitle}
            />
          </H1>
        }
        pageTitle={
          <H1 color="headerTertiary">
            <FormattedMessage
              id={messageIds.vms.notifications.notificationPageTitle}
            />
          </H1>
        }
        visible
        closable={true}
        footer={null}
        onCancel={onModalClose}
        className={className}
      >
        <List
          loading={isLoading}
          loadMore={loadMoreHistory}
          dataSource={notifications}
          renderItem={item => (
            <NotificationListItem key={item.id} notification={item} />
          )}
        />
      </Modal>
    );
  },
)<NotificationPageProps>({
  ...ant({
    '.modal-body': {
      padding: 0,
      overflow: 'auto',
      height: '70vh',
    },
  }),
});

export const NotificationPage = connect(
  state => ({
    notifications: notificationSelectors.getHistory(state),
    fullHistoryLoaded: notificationSelectors.getFullHistoryLoaded(state),
    isLoading: notificationSelectors.getIsLoadingHistory(state),
  }),
  {
    fetchHistory: notificationActions.fetchHistory,
    resetHistory: notificationActions.resetHistory,
  },
)(StyledNotificationPage);
